import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-league-mist flex items-center justify-center px-5 pt-16" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-white rounded-2xl border border-black/5 shadow-[0_25px_60px_-20px_rgba(0,74,173,0.3)] p-8 sm:p-10"
      >
        <img src="/asl-logo.png" alt="All Star League crest" className="h-20 w-auto mb-6" data-testid="admin-login-crest" />
        <h1 className="font-display text-3xl text-league-ink tracking-tight">LEAGUE CONTROL</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-8">Admin access for the All Star League content team.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-widest text-league-ink/60">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-1.5 w-full px-4 py-3 rounded-lg border border-black/10 bg-league-mist/50 text-sm focus:outline-none focus:ring-2 focus:ring-league-blue"
              placeholder="Your admin email"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-widest text-league-ink/60">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-1.5 w-full px-4 py-3 rounded-lg border border-black/10 bg-league-mist/50 text-sm focus:outline-none focus:ring-2 focus:ring-league-blue"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="text-sm font-semibold text-league-red" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full py-3.5 rounded-lg bg-league-blue text-white font-bold text-sm uppercase tracking-wider transition-colors duration-200 hover:bg-league-deep disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            Sign in
          </button>
        </form>
      </motion.div>
    </main>
  );
}
